import { usePeople, useReunions, useAddReunion, useUpdateReunion, useDeleteReunion, usePrompt } from "../hooks/hooks_index";
import { useState, useEffect, useRef } from "react";
import { ScrollToTop, Loading, MediaImage } from "../components/components_index";
import '../styles/EditReunion.css';

const token = "pk.eyJ1IjoianNwcmFkbGV5MDMiLCJhIjoiY21od2tod2I0MDBqdjJsb3U5dnhyZzBzeiJ9.mvEEnbzWVmP7S6FUVoyhqA";

export default function EditReunion() {
  const {data: reunions, reunionsLoading, reunionsError} = useReunions();
  const {data: people, peopleLoading, peopleError} = usePeople();
  const addReunion = useAddReunion();
  const updateReunion = useUpdateReunion();
  const deleteReunion = useDeleteReunion();

  const [selectedID, setSelectedID] = useState("");
  const [IDlocked, setIDlocked] = useState(false);
  const [reunionData, setReunionData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [scrollTrigger, setScrollTrigger] = useState(1);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [peopleNames, setPeopleNames] = useState([]);
  const inputRef = useRef(null);
  
  const arrayKeys = ["people"];
  const objectKeys = ["address", "start_date", "end_date"];

  const keyVisuals = {
    "year": "Year",
    "start_date": {
      "s_month": "Month",
      "s_day": "Day"
    },
    "end_date": {
      "e_month": "Month",
      "e_day": "Day"
    },
    "people": "Attendees",
    "main_pic": "Main Picture",
    "reunion_num": "Reunion Number",
    "reunion_pdfs": {
      "pdf_link": "Link",
      "pdf_title": "Title"
    },
    "address": {
      "address1": "Address",
      "address2": "Unit/Apt",
      "city": "City",
      "state": "State",
      "zip": "Zip Code",
      "formattedAddress": "Complete Address"
    }
  }

    function handleChange(e) {
    const value = e.target.value;
    setSelectedID(value)
  }

  function handleKeyPress(e) {
    if (e.key === "Escape") {
      setSelectedID("");
      e.target.blur();
    }
    if (e.key === "Enter") {
      handleSelectReunion();
    }
  }

  function handleSelectReunion() {
    if (selectedID.length === 5 && selectedID[0] === 'R') {
      setIDlocked(!IDlocked);

      if (reunions[selectedID]) {
        setReunionData(reunions[selectedID]);
      } else {
        setReunionData({})
      }
    }
  }

  function handleChangeReunion() {
    setIDlocked(!IDlocked);
    setSubmitted(false);
  }

  function updateField(key, value) {
    setReunionData(prev => ({
      ...prev,
      [key]: value
    }));
  }

  const getArray = (key) => {
    const arr = reunionData[key];
    return Array.isArray(arr) ? arr : [""];
  };

  const updateArrayItem = (key, index, value) => {
    const arr = getArray(key);
    const newArr = [...arr];
    newArr[index] = value;
    updateField(key, newArr);
  };

  const addArrayItem = (key) => {
    const arr = getArray(key);
    updateField(key, [...arr, ""]);
  };

  const removeArrayItem = (key, index) => {
    if (index > 0) {
      const arr = getArray(key);
      const newArr = arr.filter((_, i) => i !== index);
      updateField(key, newArr);
    }
  };

  const getObject = (key, subKeys) => {
    const obj = reunionData[key] || {};
    const normalized = {};
    subKeys.forEach(subKey => {
      normalized[subKey] = obj[subKey] ?? "";
    });
    return normalized;
  };

  const updateObjectItem = (key, updates) => {
    const obj = reunionData[key] || {};
    updateField(key, {
      ...obj,
      ...updates
    });
  };

  const getObjectArray = (key, subKeys) => {
    const arr = reunionData[key];
    if (!Array.isArray(arr) || arr.length === 0) {
      return [Object.fromEntries(subKeys.map(k => [k, ""]))];
    }
    return arr.map(obj =>
      Object.fromEntries(subKeys.map(k => [k, obj?.[k] ?? ""]))
    );
  };

  const updateArrayObjectItem = (key, index, subKey, value) => {
    const arr = getObjectArray(key, pdfKeys);
    const newArr = [...arr];
    newArr[index] = { ...newArr[index], [subKey]: value };
    updateField(key, newArr);
  };

  const addArrayObjectItem = (key, subKeys) => {
    const arr = getObjectArray(key, subKeys);
    const blankObj = Object.fromEntries(subKeys.map(k => [k, ""]));
    updateField(key, [...arr, blankObj]);
  };

  const removeArrayObjectItem = (key, index) => {
    const arr = getObjectArray(key, pdfKeys);
    if (arr.length > 1) {
      const newArr = arr.filter((_, i) => i !== index);
      updateField(key, newArr);
    }
  };

  function cleanReunionData(data) {
    const cleaned = {};

    for (const [key, value] of Object.entries(data)) {
      if (value === "" || value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;

        const cleanedArray = value
          .map(item => (typeof item === 'object' ? cleanReunionData(item) : item))
          .filter(item =>
            typeof item === 'object'
              ? Object.keys(item).length > 0
              : item !== ""
          );
        if (cleanedArray.length === 0) continue;
        cleaned[key] = cleanedArray;
        continue
      }
      if (typeof value === "object") {
        const cleanedObj = cleanReunionData(value);
        if (Object.keys(cleanedObj).length === 0) continue;
        cleaned[key] = cleanedObj;
        continue;
      }
      cleaned[key] = value;
    }
    if (data.address) {
      cleaned["address"]["formattedAddress"] = [
        data.address.address1,
        data.address.address2,
        `${data.address.city}, ${data.address.state} ${data.address.zip}`
      ]
      .join(", ");
    }

    return cleaned;
  }

  function handleSubmit() {
    const exists = Boolean(reunions[selectedID]);
    const cleaned = cleanReunionData(reunionData);

    const isEmpty = Object.keys(cleaned).length === 0;

    if (exists && isEmpty) {
      deleteReunion.mutate(selectedID);
    } else if (!exists && isEmpty) {
      return;
    } else if (exists) {
      updateReunion.mutate({id: selectedID, data: cleaned});
    } else {
      addReunion.mutate({id: selectedID, data: cleaned});
    }

    setSubmitted(true);
    setScrollTrigger((scrollTrigger === 1) ? 2 : 1);
  }

  function handleClear() {
    setReunionData({});
  }

  async function searchAddress(query) {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?autocomplete=true&types=address&country=us&access_token=${token}`
    );
    const data = await response.json();
    setAddressSuggestions(data.features ?? []);
  }

  async function selectSuggestion(feature) {
    const context = feature.context ?? [];

    const getContext = (type) =>
      context.find((item) => item.id.startsWith(type))?.text ?? "";

    const getShortCode = (type) =>
      context.find((item) => item.id.startsWith(type))?.short_code ?? "";

    // console.log(feature);
    // console.log(getContext("place"));

    const address1 = feature.place_name.split(",")[0];
    const city = getContext("place");
    const state = getShortCode("region").replace("US-", "");
    const zip = getContext("postcode");

    updateObjectItem("address", {
      address1,
      city,
      state,
      zip
    });
    setAddressSuggestions([]);
  }

  const renderField = (key) => {
    if (arrayKeys.includes(key)) {
      const arr = getArray(key);
      return(
        <div className="edit-input array-input">
          <label>{keyVisuals[key]}</label>
          {arr.map((val,i) => (
            <SearchBar
              peopleNames={peopleNames}
              inputRef={inputRef}
              arrKey={key} 
              i={i}
              updateArrayItem = {updateArrayItem}
              val={val}
            />
          ))}
          <button
            type="button"
            className="add-array-item"
            onClick={() => addArrayItem(key)}
          >
            Another
          </button>
          <button
            type="button"
            className="remove-array-item"
            onClick={() => removeArrayItem(key, arr.length-1)}
          >
            Delete last
          </button>
        </div>
      );
    } else if (objectKeys.includes(key)) {
      let subKeys = [];
      let keyName = "";
      if (key === "address") {
        subKeys = addressKeys;
        keyName = "Address";
      } else if (key === "start_date") {
        subKeys = sDateKeys;
        keyName = "Start Date";
      } else if (key === "end_date") {
        subKeys = eDateKeys;
        keyName = "End Date";
      }
      const obj = getObject(key, subKeys);
      return(
        <div className={`edit-input object-input${(key!=='address'? " date-obj" : "")}${(key==='address'? " address" : "")}`} key={key}>
          <label>{keyName}</label>
          {subKeys.map((subKey) => (
              <div className="edit-input object-element" key={subKey}>
                <div className={`sub-label${"address" !== key ? " day-month-label" : ""}`}>{keyVisuals[key][subKey]}</div>
                <input
                  type="text"
                  value={obj[subKey]}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateObjectItem(key, {[subKey]: value});
                    if (subKey === "address1") {
                      searchAddress(value);
                    }
                  }}
                  className={`sub-input${"address" === key ? ` ${subKey}` : ""}${"address" !== key ? " day-month" : ""}`}
                  autoComplete='batman'
                  disabled={"formattedAddress" === subKey ? true : false}
                />
                {subKey === "address1" && addressSuggestions.length > 0 && (
                  <div className="address-popup">
                    {addressSuggestions.map((suggestion) => (
                      <div
                        key={suggestion.id}
                        className="address-suggestion"
                        onClick={() => selectSuggestion(suggestion)}
                      >
                        {suggestion.place_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
          ))}
        </div>
      )
    } else if (key === "reunion_pdfs") {
      return (
        <div className="edit-input array-objects reunion-pdfs" key="reunion_pdfs">
          <label className="pdf-details">Reunion PDFs</label>
            <div className="object-box pdf-object-box">
              {getObjectArray("reunion_pdfs", pdfKeys).map((obj, i) => (
                <div className="edit-input object-input" key={i}>
                  {pdfKeys.map(subKey => (
                    <div className="edit-input object-element" key={subKey}>
                      <div className="sub-label">
                        {(() => {
                          const n = i + 1;
                          switch (n) {
                            case 1: return "1st";
                            case 2: return "2nd";
                            case 3: return "3rd";
                            default: return `${n}th`;
                          }
                        })()} {keyVisuals[key][subKey]}
                      </div>
                      <input
                        type="text"
                        value={obj[subKey]}
                        onChange={(e) =>
                          updateArrayObjectItem("reunion_pdfs", i, subKey, e.target.value)
                        }
                        autoComplete='batman'
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => removeArrayObjectItem("reunion_pdfs", i)}
                    className="delete-pdf"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          <button
            type="button"
            onClick={() => addArrayObjectItem("reunion_pdfs", pdfKeys)}
          >
            Add another PDF
          </button>
        </div>
      )
    } else {
      return(
        <div className={`edit-input${(key==='year')?' year':""}`} key={key}>
          <label className={`single-input${(key==='year')?' no-space':""}`}>{keyVisuals[key]}</label>
          <input
            type="text"
            value={reunionData[key] ?? ""}
            onChange={(e) => updateField(key, e.target.value)}
            autoComplete='batman'
            className={`${key}-box`}
          />
        </div>
      )
    }
  }

  usePrompt(
    "Are you sure you want to leave this page? Any changed data will not be saved.",
    IDlocked && !submitted
  );

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!IDlocked || submitted) return;
      
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave this page? Any changed data will not be saved.";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [IDlocked, submitted]);

  useEffect(() => {
    if (!people) return;

    setPeopleNames(
      Object.entries(people).map(([id, person]) => ({
        id,
        name: person.AKA || `${person.f_name} ${person.l_name}`,
        profile_pic: person.profile_pic
      }))
    );
  }, [people]);

  if (!people || !reunions || peopleLoading || reunionsLoading) return <Loading/>;
  if (peopleError || reunionsError) return <div>Error loading</div>;

  const reunionList = Object.entries(reunions);

  const allKeys = Object.keys(keyVisuals);
  const addressKeys = Object.keys(keyVisuals["address"]);
  const sDateKeys = Object.keys(keyVisuals["start_date"]);
  const eDateKeys = Object.keys(keyVisuals["end_date"]);
  const pdfKeys = Object.keys(keyVisuals["reunion_pdfs"]);

  return (
    <div className='edit-reunion-page' ref={inputRef}>
      <div className='reunion-select'>
        <label className="edit-ID">
          <span className={`edit-label${IDlocked ? " disabled" : ""}`}>Select a Reunion:</span>
          <select
            value={selectedID}
            onChange={(e) => setSelectedID(e.target.value)}
            disabled={IDlocked}
            id="edit-reunion-select"
          >
            <option value="">New Reunion ID</option>
            {reunionList.sort().map(([id]) => (
              <option key={id} value={id}>
                {id.slice(1,6)} Reunion
              </option>
            ))}
          </select>
        </label>
        <input
          id='new-reunion'
          type='text'
          placeholder='Add New ID'
          value={selectedID}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          disabled={IDlocked}
          autoComplete='off'
        />
        <button 
          onClick={handleSelectReunion}
          disabled={IDlocked}
          id="select-button"
        >
          Select
        </button>
        <button 
          onClick={handleChangeReunion}
          disabled={!IDlocked}
          id="change-button"
        >
          Change Reunion
        </button>
      </div>
      {(IDlocked) &&  (!submitted) &&
        <div className="edit-fields-r">
          <div className="disclaimer">If the person ID doesn't exist for a field, put the person's <b>name</b> instead</div>
          {allKeys.map(key => renderField(key))}
          <div className="bottom-buttons">
            <button
              type="button"
              className="submit-button"
              onClick={() => handleSubmit()}
            >
              Submit
            </button>
            <button
              type="button"
              className="clear-button"
              onClick={() => handleClear()}
            >
              Clear Fields
            </button>
          </div> 
        </div>
      }
      {(submitted) &&
        <div className="submitted-message">
          <div className="celebrate-wrapper">
            <div className="background-wrapper">
              <MediaImage mediaID={"Success_GIF.webp"} alt="" className="background-gif"/>
              <div className="submitted-text">
                Submitted Successfully
              </div>
            </div>
            <MediaImage mediaID={"Celebration.gif"} alt="Celebration GIF" className="celebration-gif"/>
          </div>
        </div>
      }
      {scrollTrigger && <ScrollToTop trigger={scrollTrigger} />}
    </div>
  );
}

function SearchBar ({peopleNames, inputRef, arrKey, i, updateArrayItem, val}) {
  const val_match = peopleNames.find(p => p.id === val);
  const [query, setQuery] = useState(val_match ? val_match.name : val?? "");
  const [suggestions, setSuggestions] = useState([]);
  const wrapperRef = useRef(null); 
  const popupRef = useRef(null);
  
  useEffect(() => {
    const inputBox = inputRef.current;
    if (!inputBox) return;

    function handleClick(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    }

    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);


  useEffect(() => {
    const popupEl = popupRef.current;
    if (!popupEl) return;

    function lockScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = popupEl;
      const atTop = scrollTop === 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;

      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;
 
      if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
    }

    popupEl.addEventListener("wheel", lockScroll, { passive: false });

    return () => {
      popupEl.removeEventListener("wheel", lockScroll);
    };
  }, [suggestions]);
  
  useEffect(() => {
    const match = peopleNames.find(p => p.id === val);
    setQuery(match?.name ?? val ?? "");
  }, [val, peopleNames]);

  function handleChange(e) {
    const value = e.target.value;
    setQuery(value);

    updateArrayItem(arrKey, i, value);

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    setSuggestions(
      peopleNames.filter(p =>
        p.name.toLowerCase().includes(value.toLowerCase())
      )
    );
  }

  function handleSuggestionClick(person) {
    setQuery(person.name);
    setSuggestions([]);

    updateArrayItem(arrKey, i, person.id)
  }

  useEffect(() => {
    if (!peopleNames) return;

    const match = peopleNames.find(
      p => p.name.toLowerCase() === query.toLowerCase()
    );

    updateArrayItem(
      arrKey,
      i,
      match ? match.id : query
    );
  }, [query, peopleNames]);

  return (
    <span className='person-wrapper' ref={wrapperRef}>
      {suggestions.length > 0 && (
        <div className='suggestions-popup' ref={popupRef}>
          {suggestions.map((p) => (
            <div
              key={p.id}
              className='suggestions-item'
              onClick={() => handleSuggestionClick(p)}
            >
              {p.name}
            </div>
          ))}
        </div>  
      )}
      <input
        key={`${arrKey}-${i}`}
        type="text"
        value={query}
        onChange={handleChange}
        autoComplete='batman'
      />
    </span>
  )
}